import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { useField, useFormState } from 'react-final-form';

import useProjectTypeOptions from '../../../hooks/useProjectTypeOptions';
import DropdownWithIcons from '../../../components/common-dropdown/DropdownWithIcons';
import CommonField from './common-fields/CommonField';
import CommonButton, { BUTTON_STYLES } from '../../../components/common-button/CommonButton';
import { Input } from '../../../components/common-inputs';
import CommonTextarea from '../../../components/common-textarea/CommonTextarea';
import FieldWithUserInfo from './common-fields/FieldWithUserInfo';

import scss from './ProjectRequestForm.module.scss';
import { PROJECT_TYPES } from '../../../constants';
import AdditionalSonarFields from './additional-sonar-fields/AdditionalSonarFields';

function ProjectRequestFormInner({ onSubmit }) {
  const { t } = useTranslation('projectRequest');
  const projectTypeOptions = useProjectTypeOptions();

  const navigate = useNavigate();
  const { submitting } = useFormState();

  const { input: projectTypeInput } = useField('projectType');

  const handleGoBack = () => {
    navigate(-1);
  };

  return (
    <form className={scss.formWrapper} onSubmit={onSubmit}>
      <div className={scss.fieldsContent}>
        <div className={scss.fieldsBlock}>
          <CommonField name='projectType' label={t('projectType')} isRequired>
            {({ input, meta }) => (
              <DropdownWithIcons
                value={input.value}
                options={projectTypeOptions}
                onSelect={input.onChange}
                onBlur={input.onBlur}
                onFocus={input.onFocus}
                placeholder={t('selectProjectType')}
                isError={meta.touched && meta.error}
                disabled={submitting}
              />
            )}
          </CommonField>
        </div>

        <div className={scss.fieldsBlock}>
          <FieldWithUserInfo
            name='name'
            label={t('name')}
            isRequired
            infoSelector={(state) => state.securityNew.currentUserInfo.name}>
            {({ input, meta }) => (
              <Input
                size='sm'
                value={input.value}
                onChange={input.onChange}
                onBlur={input.onBlur}
                onFocus={input.onFocus}
                placeholder={t('name')}
                isError={meta.touched && meta.error}
                disabled={submitting}
              />
            )}
          </FieldWithUserInfo>

          <FieldWithUserInfo
            name='email'
            label={t('email')}
            isRequired
            infoSelector={(state) => state.securityNew.currentUserInfo.username}>
            {({ input, meta }) => (
              <Input
                size='sm'
                value={input.value}
                onChange={input.onChange}
                onBlur={input.onBlur}
                onFocus={input.onFocus}
                placeholder={t('email')}
                isError={meta.touched && meta.error}
                disabled={submitting}
              />
            )}
          </FieldWithUserInfo>
        </div>

        <div className={scss.fieldsBlock}>
          <CommonField name='users' label={t('usersEmail')}>
            {({ input, meta }) => (
              <Input
                size='sm'
                value={input.value}
                onChange={input.onChange}
                onBlur={input.onBlur}
                onFocus={input.onFocus}
                placeholder={t('otherUsersForTheProject')}
                isError={meta.touched && meta.error}
                disabled={submitting}
              />
            )}
          </CommonField>

          <CommonField name='supplementaryInfo' label={t('supplementaryInfo')}>
            {({ input, meta }) => (
              <CommonTextarea
                value={input.value}
                onChange={input.onChange}
                onBlur={input.onBlur}
                onFocus={input.onFocus}
                placeholder={t('supplementaryInfoOrRequests')}
                isError={meta.touched && meta.error}
                rows='4'
                disabled={submitting}
              />
            )}
          </CommonField>
        </div>

        {projectTypeInput.value === PROJECT_TYPES.SONAR && <AdditionalSonarFields />}
      </div>

      <div className={scss.actions}>
        <CommonButton
          type='submit'
          styleColor={BUTTON_STYLES.PRIMARY}
          maxWidth={160}
          isLoading={submitting}
          onClick={onSubmit}>
          {t('submitForQuote')}
        </CommonButton>

        <CommonButton type='button' styleColor={BUTTON_STYLES.SECONDARY} onClick={handleGoBack} maxWidth={160}>
          {t('cancel')}
        </CommonButton>
      </div>
    </form>
  );
}

export default ProjectRequestFormInner;
